import { SOLID } from "../collision/flags.ts";
import type { CellBounds } from "../collision/things.ts";
import { COLLISION_CELL, type CollisionGrid } from "../movement/step.ts";

/**
 * Line of sight (ADR-073 decision 8): a ray over the live collision grid from a caster's origin to the centre of a
 * target's feet box, blocked by any `SOLID` cell it passes through, so no blow reaches through a wall or a closed gate.
 * Water and holes block movement, not sight (Bible 13.5). The traversal is integer throughout, with no allocation:
 * each step compares the distances to the next column and row boundary cross-multiplied, and a ray passing exactly
 * through a corner visits both cells beside it before the one across. The answer is the same from either end
 * (ADR-073's amendment of 2026-10-06): a ray tests the cell each of its points lies in and the cells it passes
 * through between them, and never a cell beyond its end, so where the end itself is a corner the walk steps only
 * along the axis that has not yet reached the end's cell. A ray to a thing (ADR-058's amendment for
 * P2.2-T7) ends where it first enters the thing's own footprint bounds, since the thing's footprint is solid in the
 * live grid and the centre of its feet box lies inside it.
 */

const NO_BOUNDS: CellBounds = Object.freeze({ left: 1, top: 1, right: 0, bottom: 0 });

/**
 * Whether a ray between two points in sixteenths crosses no `SOLID` cell. The cells of both ends count, and a cell
 * beyond the grid reads the grid's outside value.
 *
 * @param grid The live collision grid, cells `COLLISION_CELL` sixteenths a side.
 * @param fromX The start's x in sixteenths, a whole number.
 * @param fromY The start's y.
 * @param toX The end's x.
 * @param toY The end's y.
 * @returns True when nothing solid lies between.
 */
export function inSight(grid: CollisionGrid, fromX: number, fromY: number, toX: number, toY: number): boolean {
  return walk(grid, fromX, fromY, toX, toY, NO_BOUNDS, grid.outside, null);
}

/**
 * Walks a ray as `inSight` does, with the grid's edge solid whatever the grid reads beyond it, and writes the last cell
 * the ray passed through clear before it met a `SOLID` cell (ADR-095 decision 5: a projectile never leaves its map).
 * When the ray's own first cell is solid, that cell is written.
 *
 * @param grid The live collision grid.
 * @param fromX The start's x in sixteenths, a whole number.
 * @param fromY The start's y.
 * @param toX The end's x.
 * @param toY The end's y.
 * @param cell Where the last clear cell's column and row go, at 0 and 1; untouched in meaning when the ray is clear.
 * @returns True when nothing solid lies between.
 */
export function lastClearCell(grid: CollisionGrid, fromX: number, fromY: number, toX: number, toY: number, cell: Int32Array): boolean {
  return walk(grid, fromX, fromY, toX, toY, NO_BOUNDS, SOLID, cell);
}

/**
 * Whether a ray from a caster reaches a thing: it enters a cell of the thing's footprint bounds before any other
 * `SOLID` cell. At a corner in the ray's middle, a side cell that is solid outside the bounds blocks before one inside
 * them reaches; a corner that is the ray's end is entered through the end's own cell alone, so a cell beside it
 * neither blocks nor reaches.
 *
 * @param grid The live collision grid.
 * @param fromX The start's x in sixteenths, a whole number.
 * @param fromY The start's y.
 * @param toX The end's x, inside the thing's feet box.
 * @param toY The end's y.
 * @param bounds The thing's footprint bounds in collision cells (`thingCellBounds`).
 * @returns True when nothing solid lies between the caster and the thing.
 */
export function inSightOfThing(grid: CollisionGrid, fromX: number, fromY: number, toX: number, toY: number, bounds: CellBounds): boolean {
  return walk(grid, fromX, fromY, toX, toY, bounds, grid.outside, null);
}

/**
 * Walks a ray's cells: false at the first solid cell outside the bounds, true at the first cell inside them or at
 * the end.
 *
 * @param grid The grid.
 * @param fromX The start's x in sixteenths.
 * @param fromY The start's y.
 * @param toX The end's x.
 * @param toY The end's y.
 * @param bounds Cells that end the walk as reached; empty when `left` exceeds `right`.
 * @param outside The value a cell beyond the grid reads.
 * @param cell Where the last clear cell goes, or null.
 * @returns Whether the ray is clear.
 */
function walk(grid: CollisionGrid, fromX: number, fromY: number, toX: number, toY: number, bounds: CellBounds, outside: number, cell: Int32Array | null): boolean {
  let column = Math.floor(fromX / COLLISION_CELL);
  let row = Math.floor(fromY / COLLISION_CELL);
  const endColumn = Math.floor(toX / COLLISION_CELL);
  const endRow = Math.floor(toY / COLLISION_CELL);
  const dx = toX - fromX;
  const dy = toY - fromY;
  const stepX = Math.sign(dx);
  const stepY = Math.sign(dy);
  const spanX = Math.abs(dx);
  const spanY = Math.abs(dy);
  if (cell !== null) {
    cell[0] = column;
    cell[1] = row;
  }
  if (inside(bounds, column, row)) {
    return true;
  }
  if (solid(grid, column, row, outside)) {
    return false;
  }
  let steps = Math.abs(endColumn - column) + Math.abs(endRow - row);
  while ((column !== endColumn || row !== endRow) && steps > 0) {
    const toColumn = stepX === 0 ? -1 : Math.abs((stepX > 0 ? (column + 1) * COLLISION_CELL : column * COLLISION_CELL) - fromX) * spanY;
    const toRow = stepY === 0 ? -1 : Math.abs((stepY > 0 ? (row + 1) * COLLISION_CELL : row * COLLISION_CELL) - fromY) * spanX;
    const corner = stepX !== 0 && stepY !== 0 && toColumn === toRow;
    if (stepY === 0 || (stepX !== 0 && toColumn < toRow) || (corner && row === endRow)) {
      column += stepX;
      steps -= 1;
    } else if (stepX === 0 || toRow < toColumn || (corner && column === endColumn)) {
      row += stepY;
      steps -= 1;
    } else {
      const besideX = inside(bounds, column + stepX, row);
      const besideY = inside(bounds, column, row + stepY);
      if ((!besideX && solid(grid, column + stepX, row, outside)) || (!besideY && solid(grid, column, row + stepY, outside))) {
        return false;
      }
      if (besideX || besideY) {
        return true;
      }
      column += stepX;
      row += stepY;
      steps -= 2;
    }
    if (inside(bounds, column, row)) {
      return true;
    }
    if (solid(grid, column, row, outside)) {
      return false;
    }
    if (cell !== null) {
      cell[0] = column;
      cell[1] = row;
    }
  }
  return true;
}

/**
 * Whether a cell lies inside bounds.
 *
 * @param bounds The bounds, empty when `left` exceeds `right`.
 * @param column The cell's column.
 * @param row Its row.
 * @returns True inside.
 */
function inside(bounds: CellBounds, column: number, row: number): boolean {
  return column >= bounds.left && column <= bounds.right && row >= bounds.top && row <= bounds.bottom;
}

/**
 * Whether a cell is solid.
 *
 * @param grid The grid.
 * @param column The cell's column.
 * @param row Its row.
 * @param outside The value a cell beyond the grid reads.
 * @returns True when the cell, or the outside value beyond the grid, carries `SOLID`.
 */
function solid(grid: CollisionGrid, column: number, row: number, outside: number): boolean {
  const byte = column < 0 || row < 0 || column >= grid.width || row >= grid.height ? outside : (grid.cells[row * grid.width + column] as number);
  return (byte & SOLID) !== 0;
}
